import React, { useState } from 'react';
import { Alert, Button, Card, Form, Input, List, Result, Space, Spin, Tooltip, Typography } from 'antd';
import { useNavigate, useSearchParams, } from 'react-router-dom';
import Password from 'antd/es/input/Password';
import { CheckOutlined, CloseOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { passwordValidatorPattern } from "../../utils/validators";
import { useUser } from "../../context/user.context";
import { useResponsive } from "../../utils/use-responsive";
import { FormPage } from "../contents/form-page";


const { Title, Text } = Typography;

export const ForgotPasswordResetPage: React.FC<any> = () => {
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const userId = searchParams.get('userId');
  const { isTablet } = useResponsive();

  const navigate = useNavigate();
  const [isCountCorrect, setIsCountCorrect] = useState(false);
  const [isUppercase, setIsUppercase] = useState(false);
  const [isLowercase, setIsLowercase] = useState(false);
  const [isNumber, setIsNumber] = useState(false);
  const [isSpecial, setIsSpecial] = useState(false);
  const [focus, setFocus] = useState(false);

  const { forgotPasswordSubmit } = useUser();


  const onFinish = async ( values: any ) => {
    forgotPasswordSubmit.execute({
      token: token || '',
      newPassword: values.password,
      id: userId || ''
    })
      .then(() => navigate('/'))
      .catch(( error: any ) => {
        //
      });
  }

  const data = ['Minimum length 8 characters', 'Contains at least 1 number', 'Contains at least 1 lowercase letter', 'Contains at least 1 uppercase letter', 'Contains at least 1 special character'];


  function isCorrect( index: number ) {
    return (index === 0 && isCountCorrect) ||
      (index === 1 && isNumber) ||
      (index === 2 && isLowercase) ||
      (index === 3 && isUppercase) ||
      (index === 4 && isSpecial);
  }

  const text = <div style={{ padding: '20px', width: '100%' }}>
    <Space direction={'vertical'} size={'small'}>
      <Title level={5}>Requirements</Title>
      <List
        dataSource={data}
        renderItem={( item, index ) => (
          <List.Item>
            {isCorrect(index) ?
              <CheckOutlined style={{ color: 'green' }}/> :
              <CloseOutlined style={{ color: 'red' }}/>
            }
            &nbsp;
            {item}
          </List.Item>
        )}
      />
    </Space>
  </div>;

  function refreshChecklist( event: { target: { value: any; }; } ) {
    const value = event.target.value;

    const regExCount = /^.{8,256}$/;
    setIsCountCorrect(regExCount.test(value));

    const regExUppercase = /[A-Z]+/;
    setIsUppercase(regExUppercase.test(value));

    const regExLowercase = /[a-z]+/;
    setIsLowercase(regExLowercase.test(value));

    const regExNumber = /[0-9]+/;
    setIsNumber(regExNumber.test(value));

    const regExSpecial = /[|^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]+/;
    setIsSpecial(regExSpecial.test(value));
  }

  function passwordFormItem() {
    return <Form.Item
      name="password"
      label="Password"
      rules={[
        { required: true, message: 'Please input your Password!' },
        {
          pattern: passwordValidatorPattern,
          message: ''
        },
        {
          max: 24,
          message: 'Value should be less than 24 character',
        },
      ]}
    >
      <Password
        size="large"
        type="password"
        placeholder="Password"
        data-cy="signup-password-input"
        onChange={refreshChecklist}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        iconRender={( visible ) =>
          visible ? <EyeTwoTone/> : <EyeInvisibleOutlined/>
        }
      />
    </Form.Item>;
  }

  return (
    <FormPage displayFooter={true}>
      <Spin
        size={'large'}
        tip="Loading..."
        className={'full-width'}
        spinning={forgotPasswordSubmit?.loading}
      >
        {!token || !userId ? (
          <Result
            status="500"
            title="Password reset request expired"
            subTitle="Your password reset request has expired or is invalid. Please request a new reset code"
            extra={<Button type="primary" onClick={() => navigate('/forgot-password')}>Forgot password</Button>}
          />
        ) : (
          <Space direction={'vertical'} size={'small'} className={'full-width'} style={{ width: '400px' }}>
            <Space direction={'vertical'} size={'small'}>
              <Title style={{ margin: 0 }} level={2}>
                Reset password
              </Title>
              <Text type={'secondary'}></Text>
            </Space>
            <Card>
              {forgotPasswordSubmit?.error && (forgotPasswordSubmit?.error as any)?.code !== 'CodeMismatchException' && (
                <Alert className={'alert'}
                       description="Please try again. If the problem persists, contact the site administrator"
                       type="warning"
                />
              )}
              {token && (
                <Form
                  name="forgot-reset-form"
                  layout={'vertical'}
                  requiredMark={false}
                  className="signupConfirm-form"
                  onFinish={onFinish}
                >
                  <Form.Item
                    name="email"
                    label="Email"
                    initialValue={searchParams.get('email')}
                  >
                    <Input
                      size="large"
                      type="email"
                      placeholder="Email"
                      data-cy="signup-email-input"
                      disabled={true}
                    />
                  </Form.Item>
                  {isTablet ? passwordFormItem()
                    :
                    <Tooltip id="tooltip-password" placement="right" title={text} color={'white'} open={focus}
                             overlayStyle={{ width: '100%' }} overlayInnerStyle={{ width: '300px' }}>
                      {passwordFormItem()}
                    </Tooltip>
                  }
                  <Form.Item
                    name="confirm"
                    label="Confirm Password"
                    dependencies={['password']}
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: 'Please confirm your password!',
                      },
                      ( { getFieldValue } ) => ({
                        validator( _, value ) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              'The two passwords that you entered do not match!'
                            )
                          );
                        },
                      }),
                    ]}
                  >
                    <Password
                      size="large"
                      type="password"
                      placeholder="Confirm Password"
                      iconRender={( visible ) =>
                        visible ? <EyeTwoTone/> : <EyeInvisibleOutlined/>
                      }
                    />
                  </Form.Item>
                  <Space direction={'vertical'} className={'full-width'}>
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      className="signupConfirm-button full-width"
                      loading={forgotPasswordSubmit.loading}
                    >
                      Confirm
                    </Button>
                  </Space>
                </Form>
              )}
            </Card>
          </Space>
        )}
      </Spin>
    </FormPage>
  );
};
